import { type Property } from 'csstype';
import React, { useId, useLayoutEffect, useRef, useState } from 'react';

import styles from './TextInput.module.css';
import { type TextInputProps } from './TextInput.types';
import {
  cn,
  derivativeIdGenerator,
  spaceSeparatedIdListGenerator,
} from '../../utils';
import { renderCharCounter } from '../helpers/CharCounter';
import { StatefulInput, getDefaultText, getInputWidth } from '../helpers/Input';
import inputStyles from '../helpers/Input/Input.module.css';
import { Icon } from '../Icon';
import { renderInputMessage } from '../InputMessage';
import { Box } from '../layout';
import { renderLabel } from '../Typography/Label/Label.utils';
import tgStyles from '../Typography/typographyStyles.module.css';

export const TextInput = ({
  label,
  afterLabelContent,
  disabled,
  readOnly,
  errorMessage,
  tip,
  required,
  maxLength,
  onChange,
  id,
  width,
  componentSize = 'medium',
  type = 'text',
  withCharacterCounter = true,
  className,
  style,
  value,
  defaultValue,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedby,
  icon,
  prefix,
  suffix,
  ref,
  ...rest
}: TextInputProps) => {
  const [text, setText] = useState<string>(getDefaultText(value, defaultValue));
  const prefixRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);
  const [prefixLength, setPrefixLength] = useState(0);
  const [suffixLength, setSuffixLength] = useState(0);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      if (prefixRef.current) {
        setPrefixLength(prefixRef.current.offsetWidth);
      }
      if (suffixRef.current) {
        setSuffixLength(suffixRef.current.offsetWidth);
      }
    });
  }, [prefix, suffix]);

  const onChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setText(event.target.value);

    if (onChange) {
      onChange(event);
    }
  };

  const generatedId = useId();
  const uniqueId = id ?? `${generatedId}-textInput`;

  const hasErrorMessage = !!errorMessage;
  const hasTip = !!tip;
  const hasMessage = hasErrorMessage || hasTip;
  const hasBottomContainer = hasErrorMessage || hasTip || !!maxLength;
  const hasIcon = !!icon;
  const hasAffix = !!(prefix ?? suffix);

  const characterCounterId = derivativeIdGenerator(
    uniqueId,
    'characterCounter',
  );
  const tipId = derivativeIdGenerator(uniqueId, 'tip');
  const errorMessageId = derivativeIdGenerator(uniqueId, 'errorMessage');

  const inputWidth = getInputWidth(
    width,
    componentSize === 'xsmall' && 'var(--dds-input-default-width-xsmall)',
  );

  const generalInputProps = {
    ref,
    type,
    onChange: onChangeHandler,
    componentSize,
    id: uniqueId,
    hasErrorMessage,
    required,
    disabled,
    readOnly,
    maxLength,
    value,
    defaultValue,
    'aria-required': ariaRequired,
    'aria-describedby': spaceSeparatedIdListGenerator([
      hasTip ? tipId : undefined,
      hasErrorMessage ? errorMessageId : undefined,
      maxLength && withCharacterCounter ? characterCounterId : undefined,
      ariaDescribedby,
    ]),
    'aria-invalid': hasErrorMessage ? true : undefined,
    ...rest,
  };

  const showRequiredStyling = !!(required || ariaRequired);

  const prefixPaddingInlineStart: Property.PaddingInlineStart | undefined =
    prefixLength
      ? `calc(var(--dds-spacing-x1) + ${prefixLength}px)`
      : undefined;

  const suffixPaddingInlineEnd: Property.PaddingInlineEnd | undefined =
    suffixLength
      ? `calc(var(--dds-spacing-x1) + ${suffixLength}px)`
      : undefined;

  let extendedInput = null;

  const affixCns = [
    inputStyles['input-group__absolute-el'],
    styles.affix,
    disabled && styles['affix--disabled'],
  ];

  if (hasIcon) {
    extendedInput = (
      <Box
        className={cn(
          inputStyles['input-group'],
          tgStyles[`body-short-${componentSize}`],
        )}
        width={inputWidth}
      >
        <Icon
          icon={icon}
          iconSize="component"
          className={cn(
            inputStyles['input-group__absolute-el'],
            inputStyles[`input-group__absolute-el--${componentSize}`],
          )}
        />
        <StatefulInput
          className={cn(
            inputStyles[`input-with-icon--${componentSize}`],
            styles['input--extended'],
          )}
          {...generalInputProps}
        />
      </Box>
    );
  } else if (hasAffix) {
    extendedInput = (
      <Box
        position="relative"
        display="flex"
        alignItems="center"
        width={inputWidth}
      >
        {prefix && (
          <span
            ref={prefixRef}
            aria-hidden
            className={cn(
              ...affixCns,
              styles.prefix,
              disabled && styles['affix--disabled'],
            )}
          >
            {prefix}
          </span>
        )}
        <StatefulInput
          style={{
            paddingInlineStart: prefixPaddingInlineStart,
            paddingInlineEnd: suffixPaddingInlineEnd,
          }}
          className={styles['input--extended']}
          {...generalInputProps}
        />
        {suffix && (
          <span
            ref={suffixRef}
            aria-hidden
            className={cn(
              ...affixCns,
              styles.affix,
              styles.suffix,
              disabled && styles['affix--disabled'],
            )}
          >
            {suffix}
          </span>
        )}
      </Box>
    );
  }

  return (
    <div
      className={cn(
        className,
        inputStyles.container,
        styles.container,
        readOnly && styles['container--readonly'],
        disabled && styles['container--disabled'],
      )}
      style={style}
    >
      {renderLabel({
        label,
        htmlFor: uniqueId,
        showRequiredStyling,
        readOnly,
        afterLabelContent,
      })}
      {extendedInput ? (
        extendedInput
      ) : (
        <Box as={StatefulInput} width={inputWidth} {...generalInputProps} />
      )}
      {hasBottomContainer && (
        <Box
          display="flex"
          justifyContent={
            withCharacterCounter
              ? hasMessage
                ? 'space-between'
                : 'flex-end'
              : undefined
          }
          gap="x0.5"
          width={withCharacterCounter ? inputWidth : undefined}
        >
          {renderInputMessage({ tip, tipId, errorMessage, errorMessageId })}
          {renderCharCounter(
            characterCounterId,
            withCharacterCounter,
            text.length,
            maxLength,
          )}
        </Box>
      )}
    </div>
  );
};

TextInput.displayName = 'TextInput';
